import { useState, useCallback, useMemo } from "react";
import BreakdownDialog from "./BreakdownDialog";

export const LEASE_EXPIRY_BREAKDOWN_COLUMNS = [
  // { key: "reference_code", label: "Reference No." },
  { key: "asset_name", label: "Asset" },
  { key: "asset_type", label: "Asset Type" },
  { key: "assigned_code", label: "Assigned Code" },
  { key: "lessor", label: "Lessor" },
  { key: "end_date", label: "End Date", type: "date" },
  { key: "expiry_quarter", label: "Quarter" },
];

function buildLeaseExpiryBreakdownRows(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => ({
    id: item?.lease_slug || index,
    reference_code: item?.reference_code || "-",
    assigned_code: item?.assigned_code || "-",
    lessor: item.lessor || "-",
    asset_type: item?.asset_type || "-",
    asset_name: item?.asset_name || "-",
    end_date: item?.end_date,
    expiry_quarter: item?.expiry_quarter || "-",
  }));
}

// Drives the "Expiry Breakdown" modal for the lease expiry concentration chart:
export function useLeaseExpiryBreakdown({ leaseBreakdown, chartItems, onTrack }) {
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const reset = useCallback(() => {
    setSelectedQuarter(null);
    setModalOpen(false);
  }, []);

  const allRows = useMemo(() => buildLeaseExpiryBreakdownRows(leaseBreakdown), [leaseBreakdown]);
  const canRender = allRows.length > 0;
  const canFilterByQuarter = canRender && chartItems.length > 0;

  const rows = useMemo(() => {
    if (!selectedQuarter) return allRows;
    return allRows.filter((row) => row.expiry_quarter === selectedQuarter);
  }, [allRows, selectedQuarter]);

  const handleChartClick = useCallback(
    (params) => {
      if (!params || params.componentType !== "series" || !canFilterByQuarter) return;
      const quarter = chartItems[params.dataIndex]?.quarter ?? null;
      setSelectedQuarter(quarter);
      setModalOpen(true);
      onTrack?.(`Graph Bar Clicked: ${quarter || ""}`);
    },
    [chartItems, canFilterByQuarter, onTrack],
  );

  const chartEvents = useMemo(() => ({ click: handleChartClick }), [handleChartClick]);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const titleSuffix = selectedQuarter || "";

  return { rows, canRender, canFilterByQuarter, chartEvents, modalOpen, closeModal, titleSuffix, reset };
}

export function LeaseExpiryBreakdownModal({ open, onClose, rows, titleSuffix }) {
  const title = "Expiry Concentration by Quarter";
  const rowCount = Array.isArray(rows) ? rows.length : 0;
  const countLabel = `${rowCount} ${rowCount === 1 ? "Lease" : "Leases"}`;
  const subtitle = titleSuffix ? `${titleSuffix}  (${countLabel})` : countLabel;
  return (
    <BreakdownDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      exportFileName={titleSuffix ? `${title} - ${titleSuffix}` : title}
      columns={LEASE_EXPIRY_BREAKDOWN_COLUMNS}
      rows={rows}
    />
  );
}
